/**
 * What: Canonical Shopify treatment aliases and product-resolution metadata.
 * Transport / data-flow: Pure shared contract consumed by Shopify authoring and Web StudySpec promotion.
 *
 */
import * as z from 'zod';
/** Shared semantic lane and bound for complete product-description suggestions. */
export const shopifyVariationSuggestionIntentSchema = z.enum(['claim', 'description', 'offer', 'price']);
export const SHOPIFY_DESCRIPTION_VARIANT_MAX_LENGTH = 20_000;
const TREATMENT_FIELDS = {
    claim: {
        field: 'claim',
        treatmentKey: 'claim',
        identityMode: 'bound',
        resolution: { action: 'append_block', target: 'description_html' },
        requiredBaselineFields: ['descriptionHtml'],
        ideas: 'wording',
        conceptDimension: 'claim',
    },
    usage_experience: {
        field: 'usage_experience',
        treatmentKey: 'claim',
        identityMode: 'bound',
        resolution: { action: 'append_block', target: 'description_html' },
        requiredBaselineFields: ['descriptionHtml'],
        ideas: 'wording',
        conceptDimension: 'usage_experience',
    },
    ingredient_proof: {
        field: 'ingredient_proof',
        treatmentKey: 'claim',
        identityMode: 'bound',
        resolution: { action: 'append_block', target: 'description_html' },
        requiredBaselineFields: ['descriptionHtml'],
        ideas: 'wording',
        conceptDimension: 'ingredient_proof',
    },
    promotion: {
        field: 'promotion',
        treatmentKey: 'offer',
        identityMode: 'unbound',
        resolution: { action: 'advisory', target: 'none' },
        requiredBaselineFields: [],
        ideas: 'wording',
        conceptDimension: 'offer_pricing',
    },
    price: {
        field: 'price',
        treatmentKey: 'price',
        identityMode: 'bound',
        resolution: { action: 'set_price', target: 'variant_price' },
        requiredBaselineFields: ['variantPrices'],
        ideas: 'price-cards',
        conceptDimension: 'offer_pricing',
    },
    image: {
        field: 'image',
        treatmentKey: 'image_url',
        identityMode: 'bound',
        resolution: { action: 'add_featured_media', target: 'product_media' },
        requiredBaselineFields: ['productMedia'],
        ideas: 'image',
        conceptDimension: 'visual_concept',
    },
    description: {
        field: 'description',
        treatmentKey: 'description',
        identityMode: 'bound',
        resolution: { action: 'replace', target: 'description_html' },
        requiredBaselineFields: ['descriptionHtml'],
        ideas: 'wording',
        conceptDimension: 'claim',
    },
};
const TREATMENT_FIELD_BY_ALIAS = {
    claim: 'claim',
    value_proposition: 'claim',
    claim_message: 'claim',
    usage_experience: 'usage_experience',
    usage_routine: 'usage_experience',
    routine: 'usage_experience',
    how_to_use: 'usage_experience',
    directions: 'usage_experience',
    ingredient_proof: 'ingredient_proof',
    ingredient: 'ingredient_proof',
    ingredients: 'ingredient_proof',
    promotion_mechanic: 'promotion',
    offer: 'promotion',
    price: 'price',
    price_point: 'price',
    image: 'image',
    image_creative: 'image',
    description: 'description',
};
/** Why: callers must distinguish unsupported/context elements from active treatment fields. */
export function resolveShopifyTreatmentField(elementType) {
    const field = TREATMENT_FIELD_BY_ALIAS[elementType.trim().toLowerCase()];
    return field ? TREATMENT_FIELDS[field] : null;
}
/** Why: bound treatments (field replacements and claim/usage/ingredient appends) have an original-product arm; offers do not. */
export function isShopifyTreatmentIdentityBound(elementType) {
    return resolveShopifyTreatmentField(elementType)?.identityMode === 'bound';
}
//# sourceMappingURL=shopify-treatment-field.js.map